import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { useTranslation } from 'react-i18next'

import { snakeCaseToPascalCase } from '../../../../utils/helpers'
import MediaUpload from '../Media/MediaUpload'
import { augmentIncomingCreativeMedia } from '../Media/mediaPayload'
import { AssetClass, Platform, getLocaleForLanguage } from 'api/models'
import { fetchAudiences } from 'api/integrations'
import googleAudiencesApi from 'domain/googleAudiences/api'
import {
  arrayReducer,
  initStateArray,
  objectReducer,
  initStateObject,
  LOADING,
  OVERWRITE,
  FAIL,
} from 'reducers/default'
import { getUserReports } from 'api/userReports'
import Targeting from '../Edit/RunningCampaign/Targeting'
import { Keywords } from '../Edit/CampaignForm/Keywords'
import LanguageTabs from '../Edit/CampaignForm/LanguageTabs'
import CarouselAd from '../CarouselAd'
import TabSwitch from 'components/Shared/TabSwitch'
import PlatformLabel from 'components/Shared/PlatformLabel'

function translateCallToAction(value, t) {
  return t([`Recommendations.campaign.setup.call_to_action.${value}`, snakeCaseToPascalCase(value)])
}

// creative.call_to_action is polymorphic: a plain string, or a {fb, google}-
// keyed object when the TC targets both platforms with different CTAs. This
// is a single combined read-only view of the whole TC (not platform-tabbed),
// so when both platforms have a present, differing value, each gets its own
// labeled entry — mirroring the edit form's separate Meta/Google CTA fields
// (CampaignForm.jsx) — rather than a single unlabeled, deduped list.
export function getCallToActionEntries(callToAction, t) {
  if (!callToAction) return []

  if (typeof callToAction !== 'object') {
    return [{ platform: null, label: translateCallToAction(callToAction, t) }]
  }

  const fbValue = callToAction.fb || null
  const googleValue = callToAction.google || null

  if (fbValue && googleValue && fbValue !== googleValue) {
    return [
      { platform: 'meta', label: translateCallToAction(fbValue, t) },
      { platform: 'google', label: translateCallToAction(googleValue, t) },
    ]
  }

  const value = fbValue || googleValue
  return value ? [{ platform: null, label: translateCallToAction(value, t) }] : []
}

export default function RunningCampaign({
  eventId,
  tc,
  tc_run_id,
  keywords,
  creative,
  targeting,
  integrationDetails,
  tasteCluster,
  isGoogleDisplay,
  campaignTargetingGoogle,
  isMetaGoogleCombined,
  campaignTargetingFb,
  bothPlatformsSelected = false,
}) {
  const { t } = useTranslation()
  const [audiences, dispatchAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [savedAudiences, dispatchSavedAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [googleSavedAudiences, dispatchGoogleSavedAudiencesAction] = useReducer(
    arrayReducer,
    initStateArray,
  )
  const [userReports, userReportsDispatch] = useReducer(objectReducer, initStateObject)

  const tcTargeting = useMemo(
    () => (targeting || []).find((x) => x.tc === tc) ?? {},
    [targeting, tc],
  )

  const [activeLanguage, setActiveLanguage] = useState(creative.language || 'null')
  const [chosenLanguages, setChosenLanguages] = useState([])

  const hasCarouselCards = useMemo(() => {
    return creative.carousel_cards && creative.carousel_cards.length > 0
  }, [creative])

  const runningCarouselCards = useMemo(() => {
    if (!Array.isArray(creative.carousel_cards)) return []

    return creative.carousel_cards.map((card, index) => ({
      ...card,
      id: card.id || `running-carousel-${index}`,
    }))
  }, [creative.carousel_cards])

  const handleActiveLanguageChange = useCallback((newLanguage) => {
    setActiveLanguage(newLanguage)
  }, [])

  // Get content for active language
  const getCurrentLanguageContent = useCallback(() => {
    const firstLanguage = chosenLanguages[0]

    // if language === null return main fields (for past campaigns)
    if (creative.language === null && activeLanguage === 'null') {
      return {
        body: creative.body || [],
        headline: creative.headline || [],
        description: creative.description || '',
      }
    }

    // If it's the first language, return main fields
    if (firstLanguage && activeLanguage === firstLanguage.value) {
      return {
        body: creative.body || [],
        headline: creative.headline || [],
        description: creative.description || '',
      }
    }

    // For other languages, get from localizations
    const locale = getLocaleForLanguage(activeLanguage)
    const localization = creative.localizations?.[locale] || {
      bodies: [],
      headlines: [],
      description: '',
    }

    return {
      body: localization.bodies || [],
      headline: localization.headlines || [],
      description: localization.description || '',
    }
  }, [activeLanguage, chosenLanguages, creative])

  const retrieveAudiences = useCallback(() => {
    const adAccountId = (integrationDetails || []).find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
    )?.asset_id
    if (adAccountId) {
      fetchAudiences(dispatchAudiencesAction, adAccountId, 'custom')
      fetchAudiences(dispatchSavedAudiencesAction, adAccountId, 'saved')
    }
  }, [integrationDetails])

  const googleAudienceResourceNames = tcTargeting.google?.audience_resource_names ?? []

  const retrieveGoogleSavedAudiences = useCallback(async () => {
    const customerId = (integrationDetails || []).find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.google,
    )?.asset_id
    if (!customerId || !googleAudienceResourceNames.length) return

    dispatchGoogleSavedAudiencesAction({ type: LOADING })
    const response = await googleAudiencesApi.fetchAudiencesAsync(customerId, { type: 'saved' })
    if (response.error) {
      dispatchGoogleSavedAudiencesAction({ type: FAIL, payload: null })
      return
    }
    dispatchGoogleSavedAudiencesAction({ type: OVERWRITE, payload: response.data })
  }, [integrationDetails, googleAudienceResourceNames.length])

  // Meta's custom_audiences/saved_audience_id/excluded_custom_audiences are
  // {id}-only lists resolved against the fetched audiences.items/
  // savedAudiences.items; Google's audiences/excluded_audiences already
  // carry their display name inline (rich {resource_name, id, name, ...}
  // shape), so only audience_resource_names (a bare resource-name string
  // list) needs the live saved-audience fetch above to resolve a display
  // name. Grouped by platform so each can get its own PlatformLabel, mirroring
  // the CTA section above.
  const audiencesByPlatform = useMemo(() => {
    const meta = { included: [], excluded: [] }
    const google = { included: [], excluded: [] }

    const customAudienceIds = (tcTargeting.fb?.custom_audiences ?? []).map((a) => a.id)
    customAudienceIds.forEach((audience_id) => {
      const audience =
        audiences.items.length && audiences.items.find((audience) => audience.id === audience_id)
      if (audience) {
        meta.included.push(audience.name)
      }
    })

    const savedAudienceId = tcTargeting.fb?.saved_audience_id
    if (savedAudienceId) {
      const savedAudience =
        savedAudiences.items.length &&
        savedAudiences.items.find((audience) => audience.id === savedAudienceId)
      if (savedAudience) {
        meta.included.push(savedAudience.name)
      }
    }

    const excludedCustomAudienceIds = (tcTargeting.fb?.excluded_custom_audiences ?? []).map(
      (a) => a.id,
    )
    excludedCustomAudienceIds.forEach((audience_id) => {
      const audience =
        audiences.items.length && audiences.items.find((audience) => audience.id === audience_id)
      if (audience) {
        meta.excluded.push(audience.name)
      }
    })

    ;(tcTargeting.google?.audiences ?? []).forEach((audience) => {
      if (audience.name) google.included.push(audience.name)
    })

    googleAudienceResourceNames.forEach((resourceName) => {
      const match = googleSavedAudiences.items.find((a) => a.resource_name === resourceName)
      google.included.push(match?.name ?? resourceName)
    })

    ;(tcTargeting.google?.excluded_audiences ?? []).forEach((audience) => {
      if (audience.name) google.excluded.push(audience.name)
    })

    return { meta, google }
  }, [audiences, savedAudiences, googleSavedAudiences, tcTargeting, googleAudienceResourceNames])

  // Mirrors getCallToActionEntries: only split into labeled per-platform
  // cards when there's an actual difference worth showing. Both platforms
  // targeted + at least one has an audience -> one card per platform, the
  // empty side falling back to tasteCluster instead of disappearing (keeps
  // the pair visually aligned). Otherwise (single platform, or both
  // platforms empty) -> collapse to the single unlabeled fallback card
  // below.
  const audienceEntries = useMemo(() => {
    const entries = [
      { platform: 'meta', ...audiencesByPlatform.meta },
      { platform: 'google', ...audiencesByPlatform.google },
    ]
    const hasAnyAudience = entries.some(
      (entry) => entry.included.length > 0 || entry.excluded.length > 0,
    )
    return bothPlatformsSelected && hasAnyAudience
      ? entries
      : entries.filter((entry) => entry.included.length > 0 || entry.excluded.length > 0)
  }, [audiencesByPlatform, bothPlatformsSelected])

  useEffect(() => {
    retrieveAudiences()
  }, [retrieveAudiences])

  useEffect(() => {
    retrieveGoogleSavedAudiences()
  }, [retrieveGoogleSavedAudiences])

  useEffect(() => {
    getUserReports(userReportsDispatch, eventId, {
      reportType: 'WRONG_TOPIC_RECOMMENDATION',
      tc,
      tc_run_id,
    })
  }, [eventId, tc, tc_run_id])

  const reportedKeywords = useMemo(() => {
    if (userReports.content?.items) {
      return userReports.content.items.map((item) => ({
        keyword: item.keyword,
        id: item.id,
      }))
    }
    return []
  }, [userReports])

  useEffect(() => {
    const languagesFromCreative = []

    // Add main language
    if (creative.language) {
      languagesFromCreative.push({
        text: `common.language.${creative.language}`,
        value: creative.language,
      })
    }

    // Add languages from localizations sorted by index
    if (creative.localizations) {
      const sortedEntries = Object.entries(creative.localizations).sort(
        ([, a], [, b]) => (a.index || 0) - (b.index || 0),
      )

      sortedEntries.forEach(([locale]) => {
        const langCode = locale.split('_')[0]
        if (!languagesFromCreative.some((lang) => lang.value === langCode)) {
          languagesFromCreative.push({
            text: `common.language.${langCode}`,
            value: langCode,
          })
        }
      })
    }

    setChosenLanguages(languagesFromCreative)
  }, [creative])

  if (!creative) return null

  const ctaEntries = getCallToActionEntries(creative.call_to_action, t)

  return (
    <div className="recommendation__details__running">
      <Keywords
        campaign={{ tc, tc_run_id }}
        eventId={eventId}
        isCampaignRunning
        keywords={keywords}
        reportedKeywords={reportedKeywords}
        className="mb-4 pb-3"
        reportsEnabled={false}
      />

      <p className="recommendation__details__running__section__title">
        {t('Recommendations.campaign.published.media')}
      </p>

      <TabSwitch
        tabSwitch={hasCarouselCards ? 'carousel' : 'single'}
        setTabSwitch={() => {}}
        options={['single', 'carousel']}
        classNames="mb-3"
        style={{ pointerEvents: 'none', cursor: 'default' }}
        translationKey="Recommendations.campaign.setup.mediaSwitch"
      />

      {hasCarouselCards ? (
        <div className="mb-4">
          <CarouselAd cards={runningCarouselCards} readonly={true} language={creative.language} />
        </div>
      ) : Array.isArray(creative.media) ? (
        <div className="recommendation__details__running__section row recommendation__details__running__media">
          <div className="col-12">
            <MediaUpload
              onChange={() => {}}
              onError={() => {}}
              initialMedia={augmentIncomingCreativeMedia(creative)}
              readonly
            />
          </div>
        </div>
      ) : null}

      {creative.language && !hasCarouselCards && (
        <LanguageTabs
          activeLanguage={activeLanguage}
          chosenLanguages={chosenLanguages}
          onLanguageChange={handleActiveLanguageChange}
          mode="running"
        />
      )}

      <div className="recommendation__details__running__section--accent">
        {(() => {
          const currentContent = getCurrentLanguageContent()

          return (
            <>
              {currentContent.body?.length > 0 ? (
                <div className="recommendation__details__running__section">
                  <p className="recommendation__details__running__section__title">
                    {t('Recommendations.campaign.published.body')}
                  </p>

                  {currentContent.body.map((body, index) => (
                    <p key={index} className="recommendation__details__running__section__body">
                      {body}
                    </p>
                  ))}
                </div>
              ) : null}

              {currentContent.headline?.length > 0 ? (
                <div className="recommendation__details__running__section">
                  <p className="recommendation__details__running__section__title">
                    {t('Recommendations.campaign.published.headline')}
                  </p>

                  {currentContent.headline.map((headline, index) => (
                    <p key={index} className="recommendation__details__running__section__body">
                      {headline}
                    </p>
                  ))}
                </div>
              ) : null}

              {currentContent.description ? (
                <div className="recommendation__details__running__section mb-0">
                  <div className="platform-label-wrapper" style={{ marginBottom: '1rem' }}>
                    <p className="recommendation__details__running__section__title mb-0">
                      {t('Recommendations.campaign.published.description')}
                    </p>

                    <PlatformLabel platform={'meta'} bothSelected={bothPlatformsSelected} />
                  </div>

                  <p className="recommendation__details__running__section__body">
                    {currentContent.description}
                  </p>
                </div>
              ) : null}
            </>
          )
        })()}
      </div>

      {creative.destination_url ? (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.destinationUrl')}
          </p>
          {creative.destination_url.map((destination_url, index) => (
            <p key={index} className="recommendation__details__running__section__body">
              {destination_url}
            </p>
          ))}
        </div>
      ) : null}

      {ctaEntries.length > 0 && (
        <div className="recommendation__details__running__cta-row">
          {ctaEntries.map(({ platform, label }, index) => (
            <div key={platform ?? index} className="recommendation__details__running__section">
              <div className="platform-label-wrapper" style={{ marginBottom: '1rem' }}>
                <p className="recommendation__details__running__section__title mb-0">
                  {t('Recommendations.campaign.published.callToAction')}
                </p>

                {platform && <PlatformLabel platform={platform} bothSelected />}
              </div>

              <p className="recommendation__details__running__section__body">{label}</p>
            </div>
          ))}
        </div>
      )}

      {audienceEntries.length > 0 ? (
        <div className="recommendation__details__running__cta-row">
          {audienceEntries.map(({ platform, included, excluded }) => (
            <div key={platform} className="recommendation__details__running__section">
              <div className="platform-label-wrapper" style={{ marginBottom: '1rem' }}>
                <p className="recommendation__details__running__section__title mb-0">
                  {t('Recommendations.campaign.published.audience')}
                </p>

                <PlatformLabel platform={platform} bothSelected={bothPlatformsSelected} />
              </div>

              {included.length > 0 || excluded.length > 0 ? (
                <>
                  {included.map((name, index) => (
                    <p key={index} className="recommendation__details__running__section__body">
                      {name}
                    </p>
                  ))}

                  {excluded.length > 0 ? (
                    <>
                      <p className="recommendation__details__running__section__platform-label">
                        {t('Recommendations.campaign.setup.audience.excluded')}
                      </p>
                      {excluded.map((name, index) => (
                        <p
                          key={index}
                          className="recommendation__details__running__section__body recommendation__details__running__section__body--excluded"
                        >
                          {name}
                        </p>
                      ))}
                    </>
                  ) : null}
                </>
              ) : (
                <p className="recommendation__details__running__section__body">{tasteCluster}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.audience')}
          </p>

          <p className="recommendation__details__running__section__body">{tasteCluster}</p>
        </div>
      )}

      <Targeting
        tc={tc}
        tc_run_id={tc_run_id}
        targeting={targeting}
        isGoogleDisplay={isGoogleDisplay}
        campaignTargetingGoogle={campaignTargetingGoogle}
        isMetaGoogleCombined={isMetaGoogleCombined}
        campaignTargetingFb={campaignTargetingFb}
      />
    </div>
  )
}

RunningCampaign.propTypes = {
  eventId: PropTypes.string.isRequired,
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  keywords: PropTypes.array.isRequired,
  creative: PropTypes.object.isRequired,
  targeting: PropTypes.array.isRequired,
  integrationDetails: PropTypes.arrayOf(object),
  tasteCluster: PropTypes.string,
  isGoogleDisplay: PropTypes.bool,
  campaignTargetingGoogle: PropTypes.object,
  isMetaGoogleCombined: PropTypes.bool,
  campaignTargetingFb: PropTypes.object,
  bothPlatformsSelected: PropTypes.bool,
}
